import { type ReceiptDetail, PERMISSIONS, RECEIPT_STATUS_LABELS } from '@edms/contracts';
import { useQuery } from '@tanstack/react-query';
import {
  ChevronLeft,
  ChevronRight,
  Download,
  FileText,
  Loader2,
  Printer,
  RotateCw,
  X,
  ZoomIn,
  ZoomOut,
} from 'lucide-react';
import { useEffect, useState } from 'react';

import { IconButton } from '@/components/ui/primitives';
import { Badge, EmptyState, type Tone } from '@/components/ui/surfaces';
import { useAuth } from '@/features/auth/auth-context';
import { api, fetchBlobUrl } from '@/lib/api-client';
import { cn, formatAmount, formatBytes, formatDate, formatDateTime, formatPercent } from '@/lib/utils';

const STATUS_TONES: Record<ReceiptDetail['status'], Tone> = {
  IMPORTED: 'neutral',
  PROCESSING: 'info',
  OCR_PENDING: 'info',
  NEEDS_REVIEW: 'warning',
  DUPLICATE_SUSPECTED: 'warning',
  READY: 'accent',
  ARCHIVED: 'success',
  REJECTED: 'danger',
};

const ZOOM_STEPS = [0.5, 0.75, 1, 1.25, 1.5, 2, 3, 4] as const;

/**
 * Visionneuse de document (§11).
 *
 * Affiche l'aperçu haute résolution page par page avec zoom et rotation, les
 * métadonnées, les informations du lot, et les actions de téléchargement et
 * d'impression. Chaque consultation est journalisée côté serveur (§13) —
 * l'ouverture de ce panneau est donc un acte tracé, ce que l'interface assume
 * en affichant qui a archivé le document et quand.
 */
export function DocumentViewer({
  receiptId,
  onClose,
}: {
  receiptId: string;
  onClose: () => void;
}): React.JSX.Element {
  const { can } = useAuth();
  const [pageNumber, setPageNumber] = useState(1);
  const [zoomIndex, setZoomIndex] = useState(2);
  const [rotation, setRotation] = useState(0);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [imageLoading, setImageLoading] = useState(true);

  const { data: receipt, isLoading } = useQuery({
    queryKey: ['receipt', receiptId],
    queryFn: () => api.get<ReceiptDetail>(`/receipts/${receiptId}`),
  });

  const pageCount = receipt?.pages.length ?? 0;

  // Le document change : on repart de la première page, sans zoom ni rotation.
  useEffect(() => {
    setPageNumber(1);
    setZoomIndex(2);
    setRotation(0);
  }, [receiptId]);

  /*
   * L'aperçu est récupéré par `fetch` puis converti en URL d'objet : une
   * balise `<img>` ne peut pas porter d'en-tête `Authorization`, et exposer une
   * URL publique contredirait le §15. L'URL d'objet est révoquée dès qu'elle
   * n'est plus affichée, sinon chaque page consultée fuirait en mémoire.
   */
  useEffect(() => {
    if (!receipt || pageCount === 0) return;

    let revoked = false;
    let currentUrl: string | null = null;
    setImageLoading(true);

    void fetchBlobUrl(`/receipts/${receiptId}/pages/${pageNumber}`)
      .then((url) => {
        if (revoked) {
          URL.revokeObjectURL(url);
          return;
        }
        currentUrl = url;
        setImageUrl(url);
      })
      .catch(() => setImageUrl(null))
      .finally(() => {
        if (!revoked) setImageLoading(false);
      });

    return () => {
      revoked = true;
      if (currentUrl) URL.revokeObjectURL(currentUrl);
    };
  }, [receiptId, pageNumber, pageCount, receipt]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
      if (event.key === 'ArrowLeft' && pageNumber > 1) setPageNumber((page) => page - 1);
      if (event.key === 'ArrowRight' && pageNumber < pageCount) setPageNumber((page) => page + 1);
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose, pageNumber, pageCount]);

  const zoom = ZOOM_STEPS[zoomIndex] ?? 1;

  const download = async (mode: 'download' | 'print'): Promise<void> => {
    const url = await fetchBlobUrl(`/receipts/${receiptId}/document?mode=${mode}`);

    if (mode === 'print') {
      // Impression via une fenêtre dédiée : imprimer l'application entière
      // produirait la navigation et les filtres sur le papier.
      const printWindow = window.open(url, '_blank');
      printWindow?.addEventListener('load', () => printWindow.print());
      return;
    }

    const link = document.createElement('a');
    link.href = url;
    link.download = `${receipt?.searchKey ?? 'recu'}.pdf`;
    link.click();
    setTimeout(() => URL.revokeObjectURL(url), 30_000);
  };

  return (
    <aside className="flex h-full w-full flex-col border-l border-line-subtle bg-raised">
      <header className="flex h-14 shrink-0 items-center justify-between gap-3 border-b border-line-subtle px-4">
        <div className="min-w-0">
          {isLoading ? (
            <span className="text-sm text-ink-faint">Chargement…</span>
          ) : receipt ? (
            <>
              <p className="truncate font-mono text-sm font-semibold text-ink">
                {receipt.orderNumber ?? receipt.searchKey ?? '—'}
              </p>
              <p className="truncate text-2xs text-ink-faint">
                {receipt.branchCode} · lot {receipt.lotReference}
              </p>
            </>
          ) : null}
        </div>

        <div className="flex shrink-0 items-center gap-1">
          {can(PERMISSIONS.RECEIPTS_PRINT) && (
            <IconButton label="Imprimer" size="sm" onClick={() => void download('print')}>
              <Printer className="size-4" />
            </IconButton>
          )}
          {can(PERMISSIONS.RECEIPTS_DOWNLOAD) && (
            <IconButton label="Télécharger le PDF" size="sm" onClick={() => void download('download')}>
              <Download className="size-4" />
            </IconButton>
          )}
          <IconButton label="Fermer l'aperçu" size="sm" onClick={onClose}>
            <X className="size-4" />
          </IconButton>
        </div>
      </header>

      {isLoading ? (
        <div className="flex flex-1 items-center justify-center">
          <Loader2 className="size-5 animate-spin text-ink-faint" aria-hidden />
        </div>
      ) : !receipt ? (
        <EmptyState icon={FileText} title="Document introuvable" className="flex-1" />
      ) : (
        <>
          <div className="flex shrink-0 items-center justify-between gap-2 border-b border-line-subtle px-3 py-2">
            <div className="flex items-center gap-0.5">
              <IconButton
                label="Page précédente"
                size="sm"
                disabled={pageNumber <= 1}
                onClick={() => setPageNumber((page) => page - 1)}
              >
                <ChevronLeft className="size-4" />
              </IconButton>
              <span className="tabular px-1.5 text-xs text-ink-muted">
                {pageNumber} / {Math.max(1, pageCount)}
              </span>
              <IconButton
                label="Page suivante"
                size="sm"
                disabled={pageNumber >= pageCount}
                onClick={() => setPageNumber((page) => page + 1)}
              >
                <ChevronRight className="size-4" />
              </IconButton>
            </div>

            <div className="flex items-center gap-0.5">
              <IconButton
                label="Réduire"
                size="sm"
                disabled={zoomIndex <= 0}
                onClick={() => setZoomIndex((index) => Math.max(0, index - 1))}
              >
                <ZoomOut className="size-4" />
              </IconButton>
              <button
                type="button"
                onClick={() => setZoomIndex(2)}
                className="tabular min-w-12 rounded px-1 text-xs text-ink-muted transition-colors hover:text-ink"
                title="Rétablir le zoom à 100 %"
              >
                {Math.round(zoom * 100)} %
              </button>
              <IconButton
                label="Agrandir"
                size="sm"
                disabled={zoomIndex >= ZOOM_STEPS.length - 1}
                onClick={() => setZoomIndex((index) => Math.min(ZOOM_STEPS.length - 1, index + 1))}
              >
                <ZoomIn className="size-4" />
              </IconButton>
              <IconButton
                label="Pivoter de 90°"
                size="sm"
                onClick={() => setRotation((angle) => (angle + 90) % 360)}
              >
                <RotateCw className="size-4" />
              </IconButton>
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-auto bg-sunken p-4">
            {imageLoading ? (
              <div className="flex h-full items-center justify-center">
                <Loader2 className="size-5 animate-spin text-ink-faint" aria-hidden />
              </div>
            ) : imageUrl ? (
              <div className="flex min-h-full items-start justify-center">
                <img
                  src={imageUrl}
                  alt={`Page ${pageNumber} du reçu ${receipt.searchKey}`}
                  className="origin-top rounded-sm bg-white shadow-[var(--shadow-raised)] transition-transform duration-150"
                  style={{ transform: `scale(${zoom}) rotate(${rotation}deg)` }}
                />
              </div>
            ) : (
              <EmptyState
                icon={FileText}
                title="Aperçu indisponible"
                description="L'image de cette page n'a pas pu être chargée."
              />
            )}
          </div>

          <div className="max-h-[42%] shrink-0 overflow-y-auto border-t border-line-subtle">
            <MetadataPanel receipt={receipt} />
          </div>
        </>
      )}
    </aside>
  );
}

function MetadataPanel({ receipt }: { receipt: ReceiptDetail }): React.JSX.Element {
  const { can } = useAuth();

  /*
   * Le taux de reconnaissance ne s'adresse qu'à ceux qui corrigent.
   *
   * C'est un indicateur de production : il dit à l'opérateur quels champs
   * relire avant de valider un lot. Pour un compte de consultation, qui n'a
   * aucun moyen d'agir dessus, il n'apporte rien et sème le doute — « OCR
   * 84 % » sur un document par ailleurs exact laisse croire à une erreur.
   * Le document archivé fait foi ; la qualité de sa lecture automatique
   * regarde ceux qui l'ont produite.
   */
  const voitLaQualiteOcr = can(PERMISSIONS.RECEIPTS_CORRECT);

  return (
    <div className="space-y-4 p-4">
      <div className="flex flex-wrap items-center gap-2">
        <Badge tone={STATUS_TONES[receipt.status]} dot>
          {RECEIPT_STATUS_LABELS[receipt.status]}
        </Badge>
        {receipt.hasSignature ? (
          <Badge tone="success">Signature présente</Badge>
        ) : (
          <Badge tone="neutral">Sans signature</Badge>
        )}
        {voitLaQualiteOcr && receipt.ocrConfidence !== null && (
          <Badge tone={receipt.ocrConfidence >= 0.85 ? 'success' : 'warning'}>
            OCR {formatPercent(receipt.ocrConfidence)}
          </Badge>
        )}
      </div>

      <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
        <Entry label="Numéro de commande" value={receipt.orderNumber} mono />
        <Entry label="Sous-numéro" value={receipt.subNumber} mono />
        <Entry label="Date de livraison" value={formatDate(receipt.deliveryDate)} />
        <Entry label="Date d'émission" value={formatDate(receipt.issueDate)} />
        <Entry label="Client" value={receipt.customerName} span />
        <Entry label="Chauffeur" value={receipt.driverName} />
        <Entry label="Plaque" value={receipt.driverPlate} mono />
        <Entry label="Montant" value={formatAmount(receipt.totalAmount, receipt.currency)} />
        <Entry label="Pages" value={String(receipt.pageCount)} />
      </dl>

      {receipt.items.length > 0 && (
        <div className="border-t border-line-subtle pt-3">
          <p className="mb-2 text-2xs font-semibold tracking-wide text-ink-faint uppercase">
            Produits
          </p>
          <ul className="space-y-1">
            {receipt.items.map((item) => (
              <li key={item.id} className="flex justify-between gap-3 text-xs">
                <span className="truncate text-ink-muted">{item.description}</span>
                <span className="tabular shrink-0 text-ink">{item.quantity ?? '—'}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="space-y-2 border-t border-line-subtle pt-3">
        <p className="text-2xs font-semibold tracking-wide text-ink-faint uppercase">Traçabilité</p>
        <dl className="grid grid-cols-2 gap-x-4 gap-y-2">
          <Entry label="Lot" value={receipt.lotReference} mono />
          <Entry label="Bureau" value={`${receipt.branchCode} — ${receipt.branchName}`} />
          <Entry label="Numérisé par" value={receipt.operatorName} />
          <Entry label="Archivé par" value={receipt.archivedByName} />
          <Entry label="Archivé le" value={formatDateTime(receipt.archivedAt)} />
          <Entry label="Taille" value={formatBytes(receipt.fileSizeBytes)} />
          <Entry
            label="Empreinte SHA-256"
            value={`${receipt.fileHash.slice(0, 16)}…`}
            mono
            title={receipt.fileHash}
            span
          />
        </dl>
      </div>
    </div>
  );
}

function Entry({
  label,
  value,
  mono,
  span,
  title,
}: {
  label: string;
  value: string | null;
  mono?: boolean;
  span?: boolean;
  title?: string;
}): React.JSX.Element {
  return (
    <div className={cn(span && 'col-span-2')}>
      <dt className="text-2xs text-ink-faint">{label}</dt>
      <dd
        className={cn('mt-0.5 truncate text-xs text-ink', mono && 'font-mono')}
        title={title ?? value ?? undefined}
      >
        {value || '—'}
      </dd>
    </div>
  );
}
